"use client";

import { useAuth, useClerk, useSignIn } from "@clerk/nextjs";
import { useEffect, useRef, useState } from "react";
import type { AuthFlowController } from "@/features/auth/components/auth-steps";
import {
  clearSsoAttempt,
  readSsoAttempt,
} from "@/features/auth/lib/sso-attempt-storage";

/** Marks the page as the landing spot of an OAuth round trip. */
export const SSO_CALLBACK_PARAM = "sso";
export const SSO_CALLBACK_VALUE = "1";
/**
 * Clerk sends the user here when the social account cannot be used on its own
 * and a first factor — the account password — is still required.
 */
export const SSO_UNLINKED_VALUE = "unlinked";

type UseSsoReturnParams = {
  /** Value of the `sso` query param, `null` on a regular page visit. */
  ssoParam: string | null;
  flow: AuthFlowController;
  /** Where a completed social sign-in lands. */
  signInRedirectUrl: string;
  /** Where a social sign-up continues. */
  signUpRedirectUrl: string;
  signInPath: string;
  signUpPath: string;
};

/**
 * Finishes the OAuth redirect flow on the sign-in page: completes the callback,
 * or falls back to the password step when the provider account is not linked.
 */
export const useSsoReturn = ({
  ssoParam,
  flow,
  signInRedirectUrl,
  signUpRedirectUrl,
  signInPath,
  signUpPath,
}: UseSsoReturnParams) => {
  const clerk = useClerk();
  const { isLoaded } = useAuth();
  const { signIn } = useSignIn();
  // Clerk moves from `?sso=1` to `?sso=unlinked` on the same mounted page, so
  // each value is handled once rather than the hook running only once.
  const handledParam = useRef<string | null>(null);
  // Both return paths render a loader until the interrupted sign-in is placed
  // on the right step, so the e-mail form never flashes in between.
  const [isProcessing, setIsProcessing] = useState(Boolean(ssoParam));

  const { showUnlinkedAccount } = flow;

  useEffect(() => {
    // The identifier of the interrupted sign-in only exists once Clerk has
    // hydrated its client, so the callback has to wait for it.
    if (!(ssoParam && isLoaded) || handledParam.current === ssoParam) {
      return;
    }
    handledParam.current = ssoParam;

    const showUnlinkedStep = () => {
      const attempt = readSsoAttempt();
      const identifier = signIn.identifier ?? attempt?.email;

      if (identifier && attempt) {
        showUnlinkedAccount({
          identifier,
          unlinkedProvider: attempt.provider,
        });
      }

      clearSsoAttempt();
    };

    if (ssoParam === SSO_UNLINKED_VALUE) {
      showUnlinkedStep();
      setIsProcessing(false);
      return;
    }

    if (ssoParam !== SSO_CALLBACK_VALUE) {
      setIsProcessing(false);
      return;
    }

    const handleCallback = async () => {
      try {
        await clerk.handleRedirectCallback({
          continueSignUpUrl: signUpPath,
          firstFactorUrl: `${signInPath}?${SSO_CALLBACK_PARAM}=${SSO_UNLINKED_VALUE}`,
          signInForceRedirectUrl: signInRedirectUrl,
          signUpForceRedirectUrl: signUpRedirectUrl,
        });

        // Without a session Clerk has sent the user on to `?sso=unlinked`,
        // which still needs the saved attempt to show the right step.
        if (clerk.session) {
          clearSsoAttempt();
        }
      } catch {
        // The provider handshake came back without a session; the account most
        // likely exists with a password, so let the user finish that way.
        showUnlinkedStep();
      } finally {
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [
    clerk,
    isLoaded,
    showUnlinkedAccount,
    signIn,
    signInPath,
    signInRedirectUrl,
    signUpPath,
    signUpRedirectUrl,
    ssoParam,
  ]);

  return { isProcessing };
};
